<template>
  <div
    v-if="markableQuestions.length"
    ref="gradingTable"
    class="my-8"
  >
    <!-- TITLE ROW -->
    <div class="flex">
      <div
        v-if="originalGrading && !singleStageGrading"
        class="py-4 font-bold text-grey-800 w-10 text-center"
      >
        O/S
      </div>
      <div class="w-1/3">
        <div class="p-4 font-bold text-grey-800">
          Grading
        </div>
      </div>
      <div class="w-2/3 pl-8">
        <div class="flex flex-1 py-4 font-bold text-grey-800">
          <div
            v-for="keyMark in gradeRange"
            :key="keyMark"
            class="flex-1 text-center"
          >
            {{ keyMark + 1 }}
          </div>
        </div>
      </div>
    </div>
    <div class="card is-borderless">
      <!-- ROW -->
      <div
        v-for="(question, index) in markableQuestions"
        :key="question.key"
        class="grading-table-row flex"
      >
        <div
          v-if="originalGrading"
          class="flex flex-1 border-r"
        >
          <div
            v-if="!singleStageGrading"
            class="flex-1 m-1 flex items-center justify-center w-8 font-bold"
            :class="[gradingColor(originalGrading[question.key].grade, 'text')]"
          >
            {{ originalGrading[question.key].grade }}
          </div>
        </div>
        <div class="grading-table-question">
          {{ question.label }}
        </div>
        <div class="relative flex items-center">
          <popper
            trigger="click"
            :options="{
              placement: 'bottom'
            }"
            @show="toggleFocusInput(`textarea_${question.key}`, true)"
            @hide="toggleFocusInput(`textarea_${question.key}`)"
          >
            <div
              class="bg-white shadow-lg p-4 z-100 relative w-64 border-t-4 border-grey-500"
              :class="[gradingColor(grades[question.key].grade, 'border')]"
            >
              <span
                class="absolute leading-none -mb-px text-xs"
                style="left: 50%; bottom:100%; transform: translateX(-50%)"
                :class="[gradingColor(grades[question.key].grade, 'text')]"
              >
                ▲
              </span>
              <label
                :for="`${question.key}_note`"
                class="text-xs font-bold block text-grey-700 mb-1"
              >
                Notes for question {{ index + 1 }}
              </label>
              <textarea
                :id="`${question.key}_note`"
                :ref="`textarea_${question.key}`"
                :name="`${question.key}_note`"
                :value="grades[question.key].note"
                :placeholder="
                  `Please enter your notes for this question's grade`
                "
                :disabled="isLocked"
                class="focus:outline-none w-full leading-normal text-grey-600 focus:text-black transition-all transition-normal h-32 resize-none p-0"
                @blur="grades[question.key].note = $event.target.value"
              />
            </div>
            <button
              slot="reference"
              type="button"
              class="flex items-center p-3 focus:outline-none"
              :class="{
                'opacity-0 cursor-default': grades[question.key].grade === 0
              }"
              :disabled="grades[question.key].grade === 0"
            >
              <icon
                icon="chat"
                class="w-6 h-6"
                :class="{
                  'icons-primary': grades[question.key].note,
                  'icons-grey': !grades[question.key].note
                }"
              />
            </button>
          </popper>
        </div>
        <div class="grading-table-grades">
          <label
            v-for="gradeMark in gradeRange"
            :key="gradeMark"
            :for="`${question.key}_mark_${gradeMark + 1}`"
            class="flex flex-1 border-l cursor-pointer"
            tabindex="0"
            @keydown.enter="$set(grades[question.key], 'grade', gradeMark + 1)"
          >
            <input
              :id="`${question.key}_mark_${gradeMark + 1}`"
              type="radio"
              :name="`${question.key}_mark`"
              :value="gradeMark + 1"
              class="hidden"
              :disabled="isLocked"
              @change="$set(grades[question.key], 'grade', gradeMark + 1)"
            />
            <div
              class="flex-1 m-1 flex items-center justify-center transition-all transition-normal"
              :class="[
                isActive(grades[question.key].grade === gradeMark + 1),
                gradingColor(gradeMark + 1),
                {
                  'hover:opacity-100': !isLocked,
                  'cursor-not-allowed': isLocked
                }
              ]"
            >
              <icon
                v-if="grades[question.key].grade === gradeMark + 1"
                icon="check-knockout"
                class="icons-white w-6 h-6"
              />
              <span class="hidden">{{ gradeMark }}</span>
            </div>
          </label>
        </div>
      </div>
    </div>
    <div class="text-right mt-8">
      <p
        :class="{
          'text-2xl': originalTotalScore
        }"
      >
        {{ !originalTotalScore ? "Total" : "Interview" }} score:
        <strong :class="[gradingColor(gradingScore, 'text')]">
          <span
            :class="{
              'text-5xl': originalTotalScore
            }"
          >
            {{ totalScore }}
          </span>
          / {{ availableScore }}
        </strong>
        <template v-if="originalTotalScore">
          <span
            class="font-bold block text-xs"
          >Original score:
            <span
              :class="
                gradingColor(
                  Math.floor((originalTotalScore / availableScore) * 10),
                  'text'
                )
              "
            ><span class="text-base">{{ originalTotalScore }}</span>
              <span class="text-xs">/ {{ availableScore }}</span></span></span>
        </template>
      </p>
      <portal to="scoring">
        <strong
          class="text-3xl"
          :class="[gradingColor(gradingScore, 'text')]"
        ><span class="text-5xl">{{ totalScore }}</span>/{{ availableScore }}</strong>
      </portal>
    </div>
  </div>
</template>

<script>
import Popper from "vue-popperjs";
import { gradingColourMixin } from "../../mixins/gradingColour";
export default {
  name: "GradingTable",
  components: {
    Popper
  },
  mixins: [gradingColourMixin],
  props: {
    markingRange: {
      type: Number,
      default: 10
    },
    markableQuestions: {
      type: Array,
      default: null
    },
    state: {
      type: Object,
      default: null
    },
    isLocked: {
      type: Boolean,
      default: true
    },
    originalGrading: {
      type: [Object, Boolean],
      required: false,
      default: false
    },
    competition: {
      type: Object,
      required: true
    }
  },
  computed: {
    singleStageGrading() {
      return this.competition.single_stage_grading > 0;
    },
    gradeRange() {
      return Array.apply(null, { length: this.markingRange }).map(
        Number.call,
        Number
      );
    },
    totalScore() {
      return Object.keys(this.grades).reduce(
        (acc, key) => acc + this.grades[key].grade,
        0
      );
    },
    originalTotalScore() {
      if (!this.originalGrading) return;
      return Object.keys(this.originalGrading).reduce(
        (acc, key) => acc + this.originalGrading[key].grade,
        0
      );
    },
    availableScore() {
      return this.gradeRange.length * this.markableQuestions.length;
    },
    gradingScore() {
      return Math.floor((this.totalScore / this.availableScore) * 10);
    },
    grades: {
      get() {
        return this.state;
      },
      set() {
        return this.$emit("update:grade", this.grades);
      }
    }
  },
  methods: {
    toggleFocusInput(ref, focus = false) {
      if (focus) {
        setTimeout(() => this.$refs[ref][0].focus(), 50);
      }
      this.$refs[ref][0].blur();
    },
    isActive(value) {
      return value ? "opacity-100" : "opacity-25";
    }
  }
};
</script>

<style>
.grading-table-row:not(:last-child) {
  @apply border-b;
}

.grading-table-question {
  @apply w-1/3 p-4 whitespace-no-wrap overflow-hidden;
  text-overflow: ellipsis;
}

.grading-table-grades {
  @apply w-2/3 flex;
}
</style>
